import React, { FunctionComponent } from 'react'

import { Cube } from '@/icons'

import * as styles from './styles.module.css'

interface Props {
    children: React.ReactNode
}

type ChildElementType =
    | string
    | number
    | true
    | Record<string, unknown>
    | React.ReactElement<any, string | React.JSXElementConstructor<any>>

const UnknownElement = () => <p className="text-red">UNKNOWN ELEMENT</p>

export const ListWithImage: FunctionComponent<Props> = ({ children }) => {
    function renderChildElement(child: ChildElementType) {
        if (typeof child === 'number' || typeof child === 'boolean') {
            return UnknownElement
        }
        if (!React.isValidElement(child) && typeof child === 'string') {
            return <p>{child}</p>
        }
        switch (true) {
            case child.type === 'ul':
                return (
                    <div className="order-1 lg:w-3/5">
                        {React.cloneElement(child, {
                            className: `${styles.listStyles} text-black-lighter text-opacity-70 dark:text-opacity-70 dark:text-text-dm`,
                        })}
                    </div>
                )
            case typeof child.type === 'function' || child.type === 'p':
                return child.props.children.some((item) => typeof item === 'string') ? (
                    <p {...child.props} />
                ) : (
                    React.createElement(
                        'div',
                        { ...child.props, className: 'order-2 mt-18 w-full lg:mt-0 lg:w-2/5' },
                        child.props.children
                    )
                )
            default:
                return UnknownElement
        }
    }

    return (
        <div className="mt-15 mb-25 flex flex-col lg:mb-32 lg:px-5 lg:flex-row lg:gap-x-7.5">
            {React.Children.map(children, (child) => (child ? renderChildElement(child) : null))}
        </div>
    )
}

export const FeaturesBox: FunctionComponent<Props> = ({ children }) => {
    return (
        <div
            className={`${styles.featuresBoxWrapper} my-10 px-7.5 py-10 bg-white dark:bg-body-bg1-dm md:py-33 md:my-20`}
        >
            <div className="md:grid md:grid-cols-2 md:gap-x-10 md:gap-y-11 md:mx-auto md:max-w-770 lg:grid-cols-3 lg:gap-x-20 lg:max-w-1060 xl:gap-x-110">
                {children && children.map((child) => child.type !== 'br' && child)}
            </div>
        </div>
    )
}

export const Feature: FunctionComponent<Props> = ({ children }) => {
    function renderChildElement(child: ChildElementType) {
        if (typeof child === 'number' || typeof child === 'boolean') {
            return UnknownElement
        }
        if (!React.isValidElement(child) && typeof child === 'string') {
            return (
                <p className="font-light text-lg text-black-lighter text-opacity-70 dark:text-opacity-70 dark:text-text-dm">
                    {child}
                </p>
            )
        }

        if (child.type === 'strong') {
            return (
                <strong className=" text-lg text-black-lighter text-opacity-70 dark:text-opacity-70 dark:text-text-dm">
                    {child}
                </strong>
            )
        }

        return child
    }

    return (
        <div className={`${styles.featureWrapper}`}>
            <Cube />
            {React.Children.map(children, (child) => child && renderChildElement(child))}
        </div>
    )
}
